import { UNREAL_UNITS_PER_SIM_UNIT, uuToSim } from '../config';
import type { Team, Vec3 } from '../types';
import { MEDIA_KEYS, mediaAssetUrl } from '../content/MediaUrls';
import { DEFAULT_TEAM_COLORS, type TeamColors } from './TeamPresentation';

export type VehiclePaintRole = 'primary' | 'secondary' | 'original';

export interface VehicleTextureSet {
  readonly baseColorUrl?: string;
  readonly normalUrl?: string;
  readonly roughnessUrl?: string;
  readonly metalnessUrl?: string;
  readonly emissiveUrl?: string;
}

export interface VehicleMaterialBinding {
  readonly materialName: string;
  readonly paintRole?: VehiclePaintRole;
  readonly textures?: VehicleTextureSet;
}

export interface VehicleEffectSocketSpec {
  readonly position: Vec3;
  readonly direction: Vec3;
}

export interface VehicleEffectSocketSet {
  readonly boost: readonly VehicleEffectSocketSpec[];
  readonly exhaust: readonly VehicleEffectSocketSpec[];
  readonly supersonicTrail: readonly VehicleEffectSocketSpec[];
}

export interface VehicleWheelSpec {
  readonly nodeName: string;
  readonly radius: number;
  readonly steers: boolean;
}

export interface VehicleWheelRigSpec {
  readonly wheels: readonly VehicleWheelSpec[];
  readonly steeringAxis: Vec3;
  readonly rollingAxis: Vec3;
  readonly maxSteeringAngleRadians: number;
  readonly steeringResponse: number;
}

export interface VehicleBoostCosmetic {
  readonly enabled?: boolean;
  readonly outerColor?: number;
  readonly coreColor?: number;
  readonly length?: number;
  readonly radius?: number;
}

export interface VehicleSupersonicTrailCosmetic {
  readonly enabled?: boolean;
  readonly color?: number;
  readonly width?: number;
  readonly length?: number;
  readonly lifetime?: number;
  readonly emissionRate?: number;
}

export interface VehicleExhaustCosmetic {
  readonly enabled?: boolean;
  readonly color?: number;
  readonly size?: number;
  readonly lifetime?: number;
  readonly emissionRate?: number;
  readonly speed?: number;
}

export interface VehicleEffectCosmetics {
  readonly boost?: VehicleBoostCosmetic;
  readonly exhaust?: VehicleExhaustCosmetic;
  readonly supersonicTrail?: VehicleSupersonicTrailCosmetic;
}

export interface ResolvedVehicleEffectCosmetics {
  readonly boost: Required<VehicleBoostCosmetic>;
  readonly exhaust: Required<VehicleExhaustCosmetic>;
  readonly supersonicTrail: Required<VehicleSupersonicTrailCosmetic>;
}

export type VehicleModelSpec = Readonly<{
  kind: 'gltf';
  url: string;
  scale?: number;
  yawRadians?: number;
  materials?: readonly VehicleMaterialBinding[];
  effectSockets: VehicleEffectSocketSet;
  wheelRig?: VehicleWheelRigSpec;
}>;

export interface VehiclePaint {
  readonly primary: number;
  readonly secondary: number;
  /** Defaults to multiply; luminance removes texture hue before applying bound paint colors. */
  readonly textureColorMode?: 'multiply' | 'luminance';
}

export interface VehiclePresentation {
  readonly model: VehicleModelSpec;
  readonly paint: VehiclePaint;
  readonly effects?: VehicleEffectCosmetics;
}

const DEFAULT_EFFECT_COSMETICS: ResolvedVehicleEffectCosmetics = Object.freeze({
  boost: Object.freeze({
    enabled: true,
    outerColor: 0xff7417,
    coreColor: 0xffe06a,
    length: 1.05,
    radius: 0.18,
  }),
  exhaust: Object.freeze({
    enabled: true,
    color: 0xa7adb2,
    size: 0.075,
    lifetime: 0.48,
    emissionRate: 8,
    speed: 0.7,
  }),
  supersonicTrail: Object.freeze({
    enabled: true,
    color: 0xb8e9ff,
    width: 0.13,
    length: 0.8,
    lifetime: 0.22,
    emissionRate: 68,
  }),
});

const TEAM_SECONDARY_PAINTS: Readonly<Record<Team, number>> = Object.freeze({
  blue: 0x8cd5ff,
  orange: 0xffbe66,
});

const OCTANE_EFFECT_SOCKETS: VehicleEffectSocketSet = Object.freeze({
  boost: Object.freeze([
    socket(-uuToSim(9.00824), uuToSim(9.08252), uuToSim(47.6883)),
    socket(uuToSim(9.00824), uuToSim(9.08252), uuToSim(47.6883)),
  ]),
  exhaust: Object.freeze([
    socket(-uuToSim(9.00824), uuToSim(9.08252), uuToSim(47.6883)),
    socket(uuToSim(9.00824), uuToSim(9.08252), uuToSim(47.6883)),
  ]),
  supersonicTrail: Object.freeze([
    socket(-uuToSim(31.6624), uuToSim(-4.30128), uuToSim(34.2671)),
    socket(uuToSim(31.6624), uuToSim(-4.30128), uuToSim(34.2671)),
  ]),
});

const DEFAULT_OCTANE_MODEL: VehicleModelSpec = Object.freeze({
  kind: 'gltf',
  url: mediaAssetUrl(MEDIA_KEYS.vehicleOctane),
  scale: 1 / UNREAL_UNITS_PER_SIM_UNIT,
  yawRadians: Math.PI / 2,
  materials: Object.freeze([
    Object.freeze({ materialName: 'Octane_Stripes_Decal.jpg', paintRole: 'primary' as const }),
  ]),
  effectSockets: OCTANE_EFFECT_SOCKETS,
  wheelRig: Object.freeze({
    wheels: Object.freeze([
      Object.freeze({ nodeName: 'Octane-Wheel.Ft.L', radius: uuToSim(12.94926), steers: true }),
      Object.freeze({ nodeName: 'Octane-Wheel.Ft.R', radius: uuToSim(12.78939), steers: true }),
      Object.freeze({ nodeName: 'Octane-Wheel.Bk.L', radius: uuToSim(15.98674), steers: false }),
      Object.freeze({ nodeName: 'Octane-Wheel.Bk.R', radius: uuToSim(15.98674), steers: false }),
    ]),
    steeringAxis: Object.freeze({ x: 0, y: -1, z: 0 }),
    rollingAxis: Object.freeze({ x: 0, y: 0, z: -1 }),
    maxSteeringAngleRadians: Math.PI / 6,
    steeringResponse: 12,
  }),
});

export function defaultVehiclePresentation(
  team: Team,
  teamColors: TeamColors = DEFAULT_TEAM_COLORS,
): VehiclePresentation {
  return {
    model: DEFAULT_OCTANE_MODEL,
    paint: {
      primary: teamColors[team],
      secondary: TEAM_SECONDARY_PAINTS[team],
    },
  };
}

export function validateVehiclePresentation(presentation: VehiclePresentation): void {
  validateColor(presentation.paint.primary, 'primary');
  validateColor(presentation.paint.secondary, 'secondary');
  const textureColorMode = presentation.paint.textureColorMode;
  if (textureColorMode !== undefined && textureColorMode !== 'multiply' && textureColorMode !== 'luminance') {
    throw new Error('Vehicle paint texture color mode must be multiply or luminance.');
  }
  validateEffectCosmetics(presentation.effects);
  if (!presentation.model.url.trim()) throw new Error('GLTF vehicle model URL must not be empty.');
  if (presentation.model.scale !== undefined && (!Number.isFinite(presentation.model.scale) || presentation.model.scale <= 0)) {
    throw new Error('GLTF vehicle model scale must be a positive finite number.');
  }
  validateEffectSockets(presentation.model.effectSockets);
  if (presentation.model.wheelRig) validateWheelRig(presentation.model.wheelRig);
  const names = new Set<string>();
  for (const binding of presentation.model.materials ?? []) {
    const name = binding.materialName.trim();
    if (!name) throw new Error('Vehicle material binding name must not be empty.');
    if (names.has(name)) throw new Error(`Duplicate vehicle material binding "${name}".`);
    names.add(name);
    for (const url of Object.values(binding.textures ?? {})) {
      if (typeof url !== 'string' || !url.trim()) throw new Error(`Vehicle material "${name}" contains an empty texture URL.`);
    }
  }
}

function validateWheelRig(rig: VehicleWheelRigSpec): void {
  if (rig.wheels.length === 0) throw new Error('Vehicle wheel rig must declare at least one wheel.');
  validateUnitAxis(rig.steeringAxis, 'steering axis');
  validateUnitAxis(rig.rollingAxis, 'rolling axis');
  if (!Number.isFinite(rig.maxSteeringAngleRadians) || rig.maxSteeringAngleRadians <= 0) {
    throw new Error('Vehicle wheel rig max steering angle must be a positive finite number.');
  }
  if (!Number.isFinite(rig.steeringResponse) || rig.steeringResponse <= 0) {
    throw new Error('Vehicle wheel rig steering response must be a positive finite number.');
  }
  const names = new Set<string>();
  for (const wheel of rig.wheels) {
    const name = wheel.nodeName.trim();
    if (!name) throw new Error('Vehicle wheel node name must not be empty.');
    if (names.has(name)) throw new Error(`Duplicate vehicle wheel node "${name}".`);
    names.add(name);
    if (!Number.isFinite(wheel.radius) || wheel.radius <= 0) {
      throw new Error(`Vehicle wheel "${name}" radius must be a positive finite number.`);
    }
  }
}

function validateUnitAxis(axis: Vec3, label: string): void {
  validateVec3(axis, label);
  const magnitude = Math.hypot(axis.x, axis.y, axis.z);
  if (Math.abs(magnitude - 1) > 1e-4) throw new Error(`Vehicle wheel rig ${label} must be normalized.`);
}

function socket(x: number, y: number, z: number): VehicleEffectSocketSpec {
  return Object.freeze({
    position: Object.freeze({ x, y, z }),
    direction: Object.freeze({ x: 0, y: 0, z: 1 }),
  });
}

export function resolveVehicleEffectCosmetics(effects?: VehicleEffectCosmetics): ResolvedVehicleEffectCosmetics {
  return {
    boost: { ...DEFAULT_EFFECT_COSMETICS.boost, ...effects?.boost },
    exhaust: { ...DEFAULT_EFFECT_COSMETICS.exhaust, ...effects?.exhaust },
    supersonicTrail: { ...DEFAULT_EFFECT_COSMETICS.supersonicTrail, ...effects?.supersonicTrail },
  };
}

function validateEffectSockets(sockets: VehicleEffectSocketSet): void {
  for (const [kind, entries] of Object.entries(sockets)) {
    for (const [index, socket] of entries.entries()) {
      validateVec3(socket.position, `${kind}[${index}] position`);
      validateVec3(socket.direction, `${kind}[${index}] direction`);
      const magnitudeSquared = socket.direction.x ** 2 + socket.direction.y ** 2 + socket.direction.z ** 2;
      if (magnitudeSquared <= 1e-8) throw new Error(`Vehicle ${kind}[${index}] direction must not be zero.`);
    }
  }
}

function validateEffectCosmetics(effects: VehicleEffectCosmetics | undefined): void {
  if (!effects) return;
  if (effects.boost?.outerColor !== undefined) validateColor(effects.boost.outerColor, 'boost outer');
  if (effects.boost?.coreColor !== undefined) validateColor(effects.boost.coreColor, 'boost core');
  if (effects.exhaust?.color !== undefined) validateColor(effects.exhaust.color, 'exhaust');
  if (effects.supersonicTrail?.color !== undefined) validateColor(effects.supersonicTrail.color, 'supersonic trail');
  for (const [label, value] of [
    ['boost length', effects.boost?.length],
    ['boost radius', effects.boost?.radius],
    ['exhaust size', effects.exhaust?.size],
    ['exhaust lifetime', effects.exhaust?.lifetime],
    ['exhaust emission rate', effects.exhaust?.emissionRate],
    ['exhaust speed', effects.exhaust?.speed],
    ['supersonic trail width', effects.supersonicTrail?.width],
    ['supersonic trail length', effects.supersonicTrail?.length],
    ['supersonic trail lifetime', effects.supersonicTrail?.lifetime],
    ['supersonic trail emission rate', effects.supersonicTrail?.emissionRate],
  ] as const) {
    if (value !== undefined && (!Number.isFinite(value) || value <= 0)) {
      throw new Error(`Vehicle ${label} must be a positive finite number.`);
    }
  }
}

function validateVec3(value: Vec3, label: string): void {
  if (![value.x, value.y, value.z].every(Number.isFinite)) {
    throw new Error(`Vehicle ${label} must contain finite coordinates.`);
  }
}

function validateColor(value: number, label: string): void {
  if (!Number.isSafeInteger(value) || value < 0 || value > 0xffffff) {
    throw new Error(`Vehicle ${label} paint must be an RGB integer between 0x000000 and 0xFFFFFF.`);
  }
}
